import {
  getPathFromState as navigationGetPathFromState,
  getStateFromPath as navigationGetStateFromPath,
} from '@react-navigation/native'

/**
 * ATL-963 — React Navigation web/deep-link map.
 *
 * One of the four frontend crash clusters ATL-963 covers (Sentry QARASAY-KASSA-27D and -25T);
 * the others are `screens/Products/productDetailHeader.js`, `utils/savedCart.js` +
 * `utils/cartAddition.js`, and `global/Firebase/workingHours.ts`'s delete guard.
 *
 * Extracted from Routes.js so the rules below are reachable from a test — the
 * `SaleDetail` regression (Sentry QARASAY-KASSA-27D) could not be covered while this
 * object only existed inside the `Routes` component body.
 *
 * ⚠️ THE RULE FOR THIS MAP: a screen may only appear here if its ENTIRE state is
 * reconstructible from the URL. `getPathFromState` below truncates the query string, so
 * NOTHING but the pathname survives a reload — a screen listed here is therefore mounted
 * with `route.params === undefined` after a refresh / bookmark / browser back-forward.
 *
 * Param-only screens (the *Detail screens, UniversalList's live Jotai `atom`, …) must stay
 * OUT of this map. When they are absent, `getPathFromState` emits the bare route name,
 * `getStateFromPath` cannot parse it back, and the fallback below resets to Home instead of
 * mounting a screen that will throw on its first destructuring of `route.params` (ATL-632).
 *
 * `SaleDetail: 'sale-detail'` was in this map from 2026-01-01 and was missed when ATL-632
 * built that fallback, so reloading /sale-detail parsed into a VALID paramless SaleDetail
 * route — SaleDetailScreen.js `const { detail, ... } = route.params` threw, uncaught, and
 * white-screened the whole app tree.
 *
 * ── USER-VISIBLE BEHAVIOUR CHANGE FROM REMOVING IT (QARASAY-KASSA-27D) ───────────────────
 * Removing an entry changes two externally observable things, so both are recorded here and
 * pinned by `__tests__/linkingConfig.test.js`:
 *
 *   1. `https://web.atlas-app.org/sale-detail` now resets to Home instead of mounting the
 *      screen. NOTHING is lost by that: measured against the pre-fix map, `/sale-detail`
 *      parsed to `{name:'SaleDetail'}` with NO params at all, and `/sale-detail?detail=x`
 *      parsed `detail` as the STRING "x" — never a sale. `getPathFromState` below strips the
 *      query string, so the app itself never emitted a URL carrying one either. The bookmark
 *      that "stops working" was a guaranteed white screen; Home is the first state that is
 *      not a crash.
 *   2. `getPathFromState` now emits `/SaleDetail` (the bare route name) rather than
 *      `/sale-detail` when a SaleDetail is the top route — and that orphan path is exactly
 *      what the ATL-632 fallback below is for. Opening a sale from the history list emits the
 *      PARENT's path (`/sales-history`), so a reload lands the cashier on the list they came
 *      from: strictly closer to where they were than the old `/sale-detail` white screen.
 *
 * Making the deep link genuinely work is a FEATURE, not a fix, and it is two changes, not
 * one: give the screen a `:id` path segment here AND make SaleDetailScreen load the sale by
 * id (with loading and not-found states). It cannot render from today's URL — its param is
 * the whole sale object, and no id appears in the path.
 */
export const LINKING_SCREENS = {
  Login: 'login',
  NewOrgSignUp: 'signup',
  ForgotPassword: 'forgot-password',
  Home: '',
  Payments: 'payments',
  ClientOrders: 'client-orders',
  NewSale: 'new-sale',
  SupplyOrders: 'supply-orders',
  Settings: 'settings',
  Misc: 'misc',
  Shift: 'shift',
  Profile: 'profile',
  Products: 'products',
  ExportImport: 'export-import',
  SalesImport: 'sales-import',
  ManufactureImport: 'manufacture-import',
  Funnel: 'funnel',
  Paywall: 'paywall',
  BatchProductEdit: 'batch-product-edit',
  PaymentStats: 'payment-stats',
  ResetData: 'reset-data',
  PrintSettings: 'print-settings',
  Tests: 'tests',
  SalesHistory: 'sales-history',
  ProductStocks: 'stocks',
  // ATL-1694: both are whole from their URL. AiAssistants takes no params. ConnectAi's only input,
  // `?request=<id>`, is stripped by getPathFromState below — so the screen does NOT depend on the
  // param surviving: global/AiAssistants/connectAiRequest.ts captures the id from window.location
  // at load and keeps it in sessionStorage, which a reload of the bare `/connect-ai` reads back.
  // The backend's OAuth authorize endpoint redirects to exactly `/connect-ai?request=<uuid>`.
  AiAssistants: 'ai-assistants',
  ConnectAi: 'connect-ai',
}

/**
 * Screens that carry non-serializable / unreconstructible params and must never be added to
 * LINKING_SCREENS. Enumerated so the guard test can assert the two sets stay disjoint.
 */
export const PARAM_ONLY_SCREENS = [
  'SaleDetail',
  'ComplexSaleDetail',
  'ProductDetail',
  'ClientDetail',
  'EditCartItem',
  'UniversalList',
]

export const LINKING_CONFIG = {
  initialRouteName: 'Home',
  screens: LINKING_SCREENS,
}

export const getPathFromState = (state, config) => {
  let path = navigationGetPathFromState(state, config)
  const index = path.indexOf('?')
  if (index >= 0) {
    path = path.substr(0, index)
  }
  return path
}

// ATL-632: param-only screens (UniversalList's live Jotai `atom`, the *Detail screens, etc.)
// are intentionally absent from the `screens` map above, so getPathFromState emits their
// route name as the URL (e.g. /UniversalList). On back/forward the default parser can't turn
// that orphan path back into navigator state and returns undefined — which useLinking then
// hands straight to resetRoot(undefined), so the router throws "Cannot read properties of
// undefined (reading 'routes')" (web white-screen, Sentry QARASAY-KASSA-25T). These screens
// carry non-serializable params and can't be restored from a URL anyway; fall back to a valid
// initial (Home) state instead of crashing.
export const getStateFromPath = (path, config) => {
  const state = navigationGetStateFromPath(path, config)
  return state || { routes: [{ name: 'Home' }] }
}

export const buildLinkingConfig = prefix => ({
  prefixes: [
    prefix,
    'atlas://',
    'http://web.atlas-app.org',
    'qarasay-kassa://',
    'https://qarasay-kassa.netlify.app',
    'http://qarasay-kassa.web.app',
  ],
  config: LINKING_CONFIG,
  getPathFromState,
  getStateFromPath,
})
